/**
 * Replicated aspect render mode — the process-side resolver. Turns a
 * `flow X bpmn { replicate @@<key> }` directive into the `bpmnLayout`
 * `{ replicate }` option: a callee-module → aspect-place lookup that seeds one
 * `system` cylinder per consuming step.
 *
 * The place is read from the callee module's own `aspect <key>: <place>` (§7
 * membership) or `<key>: "<value>"` (classification). Every copy of the same
 * place shares one `logicalId` (`sys:<value>`), so the live shell fans
 * selection / hover / colour out to one logical node and the legend dedupes.
 */

import { moduleKey, type QualifiedName, type ResolvedModel, type ResolvedModule } from "@archlang/engine";
import type { BpmnLayoutOptions } from "@archlang/engine";
import { aspectColorFor } from "./aspect-color.js";

/** First string/identifier atom of an aspect value (lists = multi-membership,
 *  first place wins for the cylinder). */
function placeValue(value: unknown): string | undefined {
  if (!value || typeof value !== "object") return undefined;
  const v = value as { kind?: string; text?: string; items?: unknown[] };
  if (v.kind === "list") {
    for (const it of v.items ?? []) {
      const t = placeValue(it);
      if (t !== undefined) return t;
    }
    return undefined;
  }
  return (v.kind === "string" || v.kind === "identifier") && typeof v.text === "string"
    ? v.text
    : undefined;
}

/** Index every module by its dotted path key → the value it carries for `key`. */
function indexPlaces(model: ResolvedModel, key: string): Map<string, string> {
  const byPath = new Map<string, string>();
  const walk = (mods: readonly ResolvedModule[], parent: readonly string[]): void => {
    for (const m of mods) {
      const path = [...parent, m.name];
      const entry = m.body.aspects.find((a) => a.path.join(".") === key && !a.drop);
      const val = entry ? placeValue(entry.value) : undefined;
      if (val !== undefined) byPath.set(moduleKey(path), val);
      walk(m.modules, path);
    }
  };
  walk(model.modules, []);
  return byPath;
}

/**
 * Build the `bpmnLayout` replicate option for aspect plane `key`, or `undefined`
 * when nothing in the model carries it (no cylinders to draw). The returned
 * `placeOf` is pure over the resolved callee-module path.
 */
export function buildReplicateOption(
  model: ResolvedModel,
  key: string,
): BpmnLayoutOptions["replicate"] | undefined {
  const places = indexPlaces(model, key);
  if (places.size === 0) return undefined;
  return {
    key,
    placeOf: (callee: QualifiedName) => {
      const value = places.get(moduleKey(callee));
      if (value === undefined) return null;
      return { logicalId: `sys:${value}`, value, color: aspectColorFor(value) };
    },
  };
}

/** The `replicate @@<key>` plane a resolved flow-view body selects, if any. */
export function replicateKeyOf(body: readonly { readonly kind: string }[] | undefined): string | null {
  const clause = body?.find((c) => c.kind === "replicate") as { key?: string } | undefined;
  return clause?.key ?? null;
}

/**
 * Build the `bpmnLayout` engine option for aspect plane `key` — the caller-derived
 * counterpart of {@link buildReplicateOption}. Same model place-index, but each
 * step's badge is resolved from its CALLER module (`placeOf` receives
 * `fromModule`). Every copy of one value shares `eng:<value>` as its logicalId.
 * Returns `undefined` when nothing in the model carries the plane.
 */
export function buildEngineOption(
  model: ResolvedModel,
  key: string,
): BpmnLayoutOptions["engine"] | undefined {
  const places = indexPlaces(model, key);
  if (places.size === 0) return undefined;
  return {
    key,
    placeOf: (caller: QualifiedName) => {
      const value = places.get(moduleKey(caller));
      if (value === undefined) return null;
      return { logicalId: `eng:${value}`, value, color: aspectColorFor(value) };
    },
  };
}

/** The `engine @@<key>` plane a resolved flow-view body selects, if any. */
export function engineKeyOf(body: readonly { readonly kind: string }[] | undefined): string | null {
  const clause = body?.find((c) => c.kind === "engine") as { key?: string } | undefined;
  return clause?.key ?? null;
}
